import React, { useContext } from 'react';
import { ITeasersContext, TeasersContext } from 'modules/Teasers/Teasers/teasersState/teasersContext';
import DataController from 'components/DataController/DataController';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import RestaurantsList from 'components/Lists/ListView/RestaurantsList';
import Text from 'components/Text/Text';
import VideoUploaderByEntityId from 'components/VideoUploader/VideoUploaderByEntityId';
import { RequestEntity, IItemTeaser, UserRoles } from 'types';
import { teaserContentTypes } from 'appConstants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IItem {
	data: IItemTeaser;
	index: number;
}

const Item: React.FC<IItem> = ({ data, index }) => {
	const { setTeaserIDForDeletion, setTeaserIDForFullInfo } = useContext<ITeasersContext>(TeasersContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const customerRowClasses = classNames(style.customer, style.row);
	const customerNameClasses = classNames(style.customerName);
	const restaurantRowClasses = classNames(style.restaurant, style.row, style.list);
	const contentTypeRowClasses = classNames(style.contentType, style.row);
	const descriptionRowClasses = classNames(style.description, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const contentTypeName = teaserContentTypes.find((el) => el.id === data.contentType)?.name || '';

	const restaurants = data.restaurants.map((el) => ({
		...el,
		restaurantName: el.name,
	}));

	const handleClickInfo = () => {
		setTeaserIDForFullInfo(data.id);
	};

	const handleSetIdForDeletion = () => {
		setTeaserIDForDeletion(data.id);
	};

	const renderUploader = () => {
		const videoContentType =
			data.contentType === 'AssistantVideo' ||
			data.contentType === 'AdvertisingVideo' ||
			data.contentType === 'GameLaunchVideo';
		const photoContentType =
			data.contentType === 'AssistantImage' ||
			data.contentType === 'AdvertisingImage' ||
			data.contentType === 'GameLaunchImage';

		if (videoContentType) {
			return (
				<VideoUploaderByEntityId
					resource={RequestEntity.Screensavers}
					entityID={data.id}
					videoSize="Original"
					loaderSize="small"
					isShowControls={false}
				/>
			);
		}
		if (photoContentType) {
			return (
				<ImgUploaderByEntityId
					resource={RequestEntity.Screensavers}
					entityID={data.id}
					photoSize="Small"
					loaderSize="small"
					noImageIdIcon="teaserDefaultAvatar"
				/>
			);
		}
		return (
			<VideoUploaderByEntityId
				resource={RequestEntity.Screensavers}
				entityID={data.id}
				videoSize="Original"
				loaderSize="small"
				isShowControls={false}
			/>
		);
	};

	return (
		<div data-test={`Item${index}`}>
			<div className={style.container}>
				<div className={customerRowClasses}>
					<div className={style.imgWrapper}>{renderUploader()}</div>
					<Text className={customerNameClasses} translationKey={data.customerName} withTooltip={true} />
				</div>
				<div className={restaurantRowClasses}>
					<RestaurantsList restaurants={restaurants} />
				</div>
				<Text
					className={contentTypeRowClasses}
					translationNameSpace="listing"
					translationKey={contentTypeName}
					withTooltip={true}
				/>
				<Text className={descriptionRowClasses} translationKey={data.description} withTooltip={true} />
				<div className={actionRowClasses}>
					<DataController
						onClickInfo={handleClickInfo}
						onClickDelete={hasAccess ? handleSetIdForDeletion : undefined}
						URLWithIDToEdit={hasAccess ? `/teasers/edit?teaserID=${data.id}` : undefined}
						size="smallBtn"
						isOpenInfo={false}
					/>
				</div>
			</div>
		</div>
	);
};

export default Item;
